import { useEffect, useState, useContext, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import "../sidebar-stylesheet.css";
import { Tree } from "primereact/tree";
import "primereact/resources/themes/bootstrap4-light-blue/theme.css"; // Tema de Base
import "primereact/resources/primereact.min.css";
import useApi from "../../../scripts/apiService";
import TreeContext from "../../../context/treeContext";
import { useTranslation } from "react-i18next";
import { useToken } from "../../../scripts/tokenContext";
import { useMarquee } from "./useMarquee";

// `showFilterHighlight`: o filtro de ativo só atua na Home, então só ali a
// árvore o reflete. Fora da Home o filtro continua existindo no contexto — ele
// apenas não destaca, não expande e não rola nada. O padrão é `false` para que
// esquecer a prop erre pelo lado seguro: sem vazamento do filtro.
function AssetsTree({ showFilterHighlight = false }) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [expandedKeys, setExpandedKeys] = useState({});
  const { fetchData } = useApi();
  const {
    setTreeSelected,
    treeSelected,
    treeData,
    setTreeData,
    treeScreen,
    treeFilter,
  } = useContext(TreeContext);
  const initialFetchDone = useRef(false);
  const [tokenReady, setTokenReady] = useState(false);
  const { permissions } = useToken();

  useEffect(() => {
    // Aguarda o token estar disponível antes de buscar os dados
    const user = JSON.parse(localStorage.getItem("user"));
    if (user && user.token) {
      setTokenReady(true);
    }
  }, []);

  const hasAssetCreationPermission = (requestedPermissions) => {
    if (!permissions) return false;
    const requested = Array.isArray(requestedPermissions)
      ? requestedPermissions
      : [requestedPermissions];
    return requested.some((slug) => permissions.some((p) => p.slug === slug));
  };

  const getCreateAssetLabel = useCallback(
    (type) => {
      switch (type) {
        case "C":
          return `+ ${t("Criar")} ${t("Cliente")}`;
        case "U":
          return `+ ${t("Criar")} ${t("Unidade")}`;
        case "A":
          return `+ ${t("Criar")} ${t("Área")}`;
        case "E":
          return `+ ${t("Criar")} ${t("Equipamento")}`;
        case "M":
          return `+ ${t("Criar")} ${t("Mancal")}`;
        case "PL":
          return `+ ${t("Criar")} ${t("Ponto de Lubrificação")}`;
        default:
          return null;
      }
    },
    [t],
  );

  const relabelAssetNodes = useCallback(
    (nodes = []) =>
      nodes.map((node) => {
        const nextNode = { ...node };
        const type = node.data?.type;

        if (typeof type === "string" && type.startsWith("create-")) {
          const normalizedType = type.replace("create-", "");
          const translatedLabel = getCreateAssetLabel(normalizedType);

          if (translatedLabel) {
            nextNode.label = translatedLabel;
          }
        }

        if (node.children) {
          nextNode.children = relabelAssetNodes(node.children);
        }

        return nextNode;
      }),
    [getCreateAssetLabel],
  );

  useEffect(() => {
    if (!tokenReady) return;
    /* istanbul ignore next -- defensive: this effect (dep [tokenReady]) runs once when tokenReady flips false->true, and initialFetchDone is set inside it, so a re-entry with the flag already true never happens */
    if (initialFetchDone.current) return;
    /* istanbul ignore next -- defensive: reaching here means tokenReady became true, which only happens when localStorage held a user with a token, and fetchData is always provided by useApi */
    if (!fetchData || !localStorage.getItem("user")) return;
    const user = JSON.parse(localStorage.getItem("user"));
    /* istanbul ignore next -- defensive: same precondition as above — tokenReady===true implies the stored user carries a token */
    if (!user || !user.token) return;

    initialFetchDone.current = true;
    setTreeSelected(null);

    const fetchInitialNodes = async () => {
      setLoading(true);
      const clientId = user?.user?.client?.id; // Obtém o client_id do usuário

      if (clientId) {
        await fetchData(`/cascade-nodes?client_id=${clientId}`)
          .then((response) => {
            const sortedUnit = response.data.sort((a, b) => {
              const nameA = a.name || a.code;
              const nameB = b.name || b.code;
              return nameA.localeCompare(nameB);
            });
            const canCreateUnits = hasAssetCreationPermission([
              "create_cascade_nodes",
              "delete_cascade_nodes",
            ]);
            const formattedData = sortedUnit.map((unit) => {
              // calcLeaf: prioridade -> se não tem filhos e não pode criar => leaf true
              // depois considerar treeScreen e fallback baseado em has_children
              const calcLeaf =
                !unit?.has_children && !canCreateUnits
                  ? true
                  : treeScreen
                    ? false
                    : !unit?.has_children;

              return {
                key: `${unit?.label}`,
                label: unit?.name
                  ? `${unit?.name} (${unit?.code})`
                  : unit?.code,
                leaf: calcLeaf,
                data: unit,
              };
            });
            setTreeData([
              ...(treeScreen && canCreateUnits
                ? [
                    {
                      key: "create-U",
                      label: `+ ${t("Criar")} ${t("Unidade")}`,
                      leaf: true,
                      data: { type: "create-U" },
                    },
                  ]
                : []),
              ...formattedData,
            ]);
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        await fetchData("/clients")
          .then((response) => {
            const sortedUnit = response.data.sort((a, b) => {
              const nameA = a.name || a.code;
              const nameB = b.name || b.code;
              return nameA.localeCompare(nameB);
            });

            const canCreateClients = hasAssetCreationPermission([
              "create_clients",
              "delete_clients",
            ]);

            const formattedClients = sortedUnit.map((client) => {
              const calcLeaf =
                !client?.has_children && !canCreateClients
                  ? true
                  : treeScreen
                    ? false
                    : !client?.has_children;

              return {
                key: `${client?.code}`,
                label: client?.name
                  ? `${client?.name} (${client?.code})`
                  : client?.code,
                leaf: calcLeaf,
                data: client,
              };
            });

            setTreeData([
              ...(treeScreen && canCreateClients
                ? [
                    {
                      key: "create-C",
                      label: `+ ${t("Criar")} ${t("Cliente")}`,
                      leaf: true,
                      data: { type: "create-C" },
                    },
                  ]
                : []),
              ...formattedClients,
            ]);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    };
    fetchInitialNodes();
  }, [tokenReady]); // mantido tokenReady como gatilho inicial

  useEffect(() => {
    setTreeData((prevTreeData) => relabelAssetNodes(prevTreeData));
  }, [i18n.language, relabelAssetNodes, setTreeData]);

  // Atualiza a árvore sempre que treeScreen mudar
  useEffect(() => {
    if (!tokenReady) return;
    if (typeof treeScreen !== "undefined") {
      const fetchInitialNodes = async () => {
        setLoading(true);
        const user = JSON.parse(localStorage.getItem("user"));
        const clientId = user?.user?.client?.id;
        if (clientId) {
          await fetchData(`/cascade-nodes?client_id=${clientId}`)
            .then((response) => {
              const sortedUnit = response.data.sort((a, b) => {
                const nameA = a.name || a.code;
                const nameB = b.name || b.code;
                return nameA.localeCompare(nameB);
              });

              const canCreateUnits = hasAssetCreationPermission([
                "create_cascade_nodes",
                "delete_cascade_nodes",
              ]);

              const formattedData = sortedUnit.map((unit) => {
                const calcLeaf =
                  !unit?.has_children && !canCreateUnits
                    ? true
                    : treeScreen
                      ? false
                      : !unit?.has_children;

                return {
                  key: `${unit?.label}`,
                  label: unit?.name
                    ? `${unit?.name} (${unit?.code})`
                    : unit?.code,
                  leaf: calcLeaf,
                  data: unit,
                };
              });

              setTreeData([
                ...(treeScreen && canCreateUnits
                  ? [
                      {
                        key: "create-U",
                        label: `+ ${t("Criar")} ${t("Unidade")}`,
                        leaf: true,
                        data: { type: "create-U" },
                      },
                    ]
                  : []),
                ...formattedData,
              ]);
            })
            .finally(() => {
              setLoading(false);
            });
        } else {
          await fetchData("/clients")
            .then((response) => {
              const sortedUnit = response.data.sort((a, b) => {
                const nameA = a.name || a.code;
                const nameB = b.name || b.code;
                return nameA.localeCompare(nameB);
              });

              const canCreateClients = hasAssetCreationPermission([
                "create_clients",
                "delete_clients",
              ]);

              const formattedClients = sortedUnit.map((client) => {
                const calcLeaf =
                  !client?.has_children && !canCreateClients
                    ? true
                    : treeScreen
                      ? false
                      : !client?.has_children;

                return {
                  key: `${client?.code}`,
                  label: client?.name
                    ? `${client?.name} (${client?.code})`
                    : client?.code,
                  leaf: calcLeaf,
                  data: client,
                };
              });

              setTreeData([
                ...(treeScreen && canCreateClients
                  ? [
                      {
                        key: "create-C",
                        label: `+ ${t("Criar")} ${t("Cliente")}`,
                        leaf: true,
                        data: { type: "create-C" },
                      },
                    ]
                  : []),
                ...formattedClients,
              ]);
            })
            .finally(() => {
              setLoading(false);
            });
        }
      };
      fetchInitialNodes();
    }
  }, [treeScreen, tokenReady]);

  const loadOnExpand = async (event) => {
    // Sempre busca filhos se não houver filhos reais (exceto para pontos de lubrificação)
    if (
      !event.node.children ||
      event.node.children.every(
        (child) =>
          child.data &&
          child.data.type &&
          child.data.type.startsWith("create-"),
      )
    ) {
      setLoading(true);

      const nodeData = event.node.data || {};
      let childEndpoint = "";
      let createType = null;
      let requiredPerms = [];

      // Nível pelo discriminador `type` de cascade_nodes. Ponto de lubrificação
      // (folha) tem cascade_node_id/grease_id próprios; cliente (topo) não tem nenhum.
      // Filhos: /cascade-nodes?parent_id= (área/equip/mancal sob um nó),
      // /cascade-nodes?client_id= (unidades do cliente) ou
      // /lubrication-spots?cascade_node_id= (pontos de um mancal).
      if (nodeData.cascade_node_id || nodeData.grease_id) {
        setLoading(false); // ponto de lubrificação = folha, não expande
        return;
      } else if (nodeData.node_type === "unit") {
        childEndpoint = `/cascade-nodes?parent_id=${nodeData.id}`;
        createType = "A";
        requiredPerms = ["create_cascade_nodes", "delete_cascade_nodes"];
      } else if (nodeData.node_type === "area") {
        childEndpoint = `/cascade-nodes?parent_id=${nodeData.id}`;
        createType = "E";
        requiredPerms = ["create_cascade_nodes", "delete_cascade_nodes"];
      } else if (nodeData.node_type === "equipment") {
        childEndpoint = `/cascade-nodes?parent_id=${nodeData.id}`;
        createType = "M";
        requiredPerms = ["create_cascade_nodes", "delete_cascade_nodes"];
      } else if (nodeData.node_type === "bearing") {
        childEndpoint = `/lubrication-spots?cascade_node_id=${nodeData.id}`;
        createType = "PL";
        requiredPerms = [
          "create_lubrication_spots",
          "delete_lubrication_spots",
        ];
      } else if (nodeData.code && nodeData.id) {
        childEndpoint = `/cascade-nodes?client_id=${nodeData.id}`;
        createType = "U";
        requiredPerms = ["create_cascade_nodes", "delete_cascade_nodes"];
      } else {
        console.error(
          "Não foi possível identificar o tipo do nó pelo objeto:",
          nodeData,
        );
        setLoading(false);
        return;
      }
      const createLabel = createType ? getCreateAssetLabel(createType) : "";
      setTreeData([...treeData]);

      try {
        const response = await fetchData(childEndpoint);

        const sortedChildren = response.data.sort((a, b) => {
          const nameA = a.name || a.code;
          const nameB = b.name || b.code;
          return nameA.localeCompare(nameB);
        });

        const canCreateChildren = hasAssetCreationPermission(requiredPerms);

        let children = sortedChildren.map((item) => {
          const isLubSpot = childEndpoint.includes("/lubrication-spots");

          // calcLeaf: se não tem filhos e não pode criar => leaf true
          // depois considerar treeScreen e fallback baseado em has_children
          const calcLeaf = isLubSpot
            ? true
            : !item.has_children && !canCreateChildren
              ? true
              : treeScreen
                ? false
                : !item.has_children;

          return {
            key: `${item.label}`,
            label: item?.name ? `${item?.name} (${item?.code})` : item?.code,
            leaf: calcLeaf,
            data: item,
          };
        });

        // Só insere nó de criação se o usuário tiver permissão
        if (treeScreen && canCreateChildren && createType) {
          const parentKey = event.node.key;
          children = [
            {
              key: `create-${createType}-${parentKey}`,
              label: createLabel,
              leaf: true,
              data: { type: `create-${createType}`, parent: event.node.data },
            },
            ...children,
          ];
        }

        // fallback: só criar nó de criação quando realmente pode criar
        const finalChildren =
          children.length === 0 && treeScreen && createType && canCreateChildren
            ? [
                {
                  key: `create-${createType}-${event.node.key}`,
                  label: createLabel,
                  leaf: true,
                  data: {
                    type: `create-${createType}`,
                    parent: event.node.data,
                  },
                },
              ]
            : children;

        // Importante: não atribuir children = [] porque PrimeReact mostra chevron para arrays vazios.
        if (finalChildren.length === 0) {
          delete event.node.children;
          event.node.leaf = true;
        } else {
          event.node.children = finalChildren;
        }
        setTreeData([...treeData]);
      } catch (error) {
        console.error("Erro ao carregar filhos:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  // Função para seleção/deseleção de nó
  const handleNodeSelect = (event) => {
    if (
      typeof treeSelected === "object" &&
      treeSelected?.key === event.node.key
    ) {
      setTreeSelected(null);
    } else {
      setTreeSelected(event.node);
    }
  };

  useEffect(() => {
    setExpandedKeys({}); // Reseta todos os chevrons ao mudar treeScreen
  }, [treeScreen]);

  // Auto-expandir (soft) o caminho do nó filtrado e rolar até ele para que o
  // destaque "selected-node" fique visível ao aplicar um filtro. Best-effort: se
  // o nó não estiver na treeData já carregada (ramo profundo carregado sob
  // demanda), degrada graciosamente — sem forçar buscas nem quebrar keys em
  // string. A pílula persistente no cabeçalho mantém o indicador de filtro
  // independentemente disso. Não fixa/trava: o usuário pode recolher depois.
  useEffect(() => {
    // Fora da Home o filtro não atua: nada de expandir nem rolar por causa dele.
    if (!showFilterHighlight) return undefined;
    if (!treeFilter?.key) return undefined;
    const ancestors = findAncestorKeys(treeData, treeFilter.key);
    // ancestors === null: nó ainda não carregado -> nada a fazer (degrada).
    if (!ancestors) return undefined;
    if (ancestors.length > 0) {
      setExpandedKeys((prev) => {
        const next = { ...prev };
        ancestors.forEach((key) => {
          next[key] = true;
        });
        return next;
      });
    }
    // Rola o nó destacado para a área visível após a expansão renderizar.
    const timer = setTimeout(() => {
      const el = document.querySelector(".tree-sidebar .selected-node");
      /* istanbul ignore next -- scrollIntoView não existe no jsdom; só roda no browser real */
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "center", inline: "nearest" });
      }
    }, 0);
    return () => clearTimeout(timer);
    // Dispara ao mudar o filtro; treeData é lido do render atual (o nó
    // selecionado já está carregado no momento em que o filtro é aplicado).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeFilter, showFilterHighlight]);

  // A chave do nó selecionado, reduzida a uma forma só. `treeSelected` aparece
  // em três formatos: o nó da árvore (`{ key, label, leaf, data }`), a string
  // da key (modo de criação) e um objeto de deep-link SEM key (tree.js) — que
  // não aponta para nenhum nó da árvore e por isso não conta como seleção.
  const selectedNodeKey =
    typeof treeSelected === "string" ? treeSelected : treeSelected?.key;

  // Customiza o rótulo do nó: quando o texto excede a largura, mostra "…" em
  // repouso e, ao passar o mouse, faz um marquee de VELOCIDADE CONSTANTE que
  // rola só até expor o final e para (mesmo comportamento da pílula de filtro).
  const useNodeTemplate = (node) => {
    const { outerRef, innerRef, marquee, marqueeVars } = useMarquee(node.label);

    // Duas regras, nesta ordem:
    // 1. Fora da Home o filtro não destaca nada — só a seleção real conta.
    // 2. Na Home, a seleção tem PRIORIDADE sobre o filtro: só cai para o
    //    treeFilter quando não há nó selecionado. Com o `||` que havia aqui,
    //    seleção e filtro somavam e DOIS nós ficavam destacados.
    // O teste é pela CHAVE, não pela truthiness de treeSelected: o deep-link
    // (tree.js) grava um treeSelected SEM `key`, que não aponta para nó algum.
    const isSelected = selectedNodeKey
      ? selectedNodeKey === node.key
      : showFilterHighlight && treeFilter?.key === node.key;

    return (
      <span
        ref={outerRef}
        className={`tree-node-label${isSelected ? " selected-node" : ""}${
          marquee.overflow ? " is-overflow" : ""
        }`}
        style={{
          display: "inline-block",
          whiteSpace: "nowrap",
          maxWidth: "var(--tree-node-label-max-width)",
          overflow: "hidden",
          verticalAlign: "middle",
          position: "relative",
        }}
        title={node.label}
      >
        <span
          ref={innerRef}
          className="tree-node-label-inner"
          style={{
            display: "inline-block",
            whiteSpace: "nowrap",
            ...marqueeVars,
          }}
        >
          {node.label}
        </span>
      </span>
    );
  };

  return (
    <>
      <Tree
        value={treeData}
        expandedKeys={expandedKeys}
        onToggle={(e) => setExpandedKeys(e.value)}
        selectionMode={"single"}
        onExpand={loadOnExpand}
        loading={loading}
        // Mesmas duas regras do `isSelected` acima, e pela mesma chave: sem o
        // teste por `selectedNodeKey`, um treeSelected sem `key` produzia
        // `{ undefined: true }` e apagava também a moldura do nó filtrado.
        selectionKeys={
          selectedNodeKey
            ? { [selectedNodeKey]: true }
            : showFilterHighlight && treeFilter?.key
              ? { [treeFilter.key]: true }
              : {}
        }
        onSelectionChange={(e) => {
          let selectedKey = null;
          /* istanbul ignore else -- defensive: with selectionMode="single" the Tree emits the key as a string (or null), never an object; the else-if object branch only occurs in multiple/checkbox selection */
          if (typeof e.value === "string") {
            selectedKey = e.value;
          } else if (e.value && typeof e.value === "object") {
            selectedKey = Object.keys(e.value)[0];
          }
          const selectedNode = findNodeByKey(treeData, selectedKey);
          // Se for nó de criação, seta o tipo correto e a key completa
          if (
            selectedNode &&
            selectedNode.data &&
            selectedNode.data.type &&
            selectedNode.data.type.startsWith("create-")
          ) {
            // Passe a key completa para treeSelected, não só o tipo
            setTreeSelected(selectedNode.key);
          } else if (selectedNode) {
            handleNodeSelect({ node: selectedNode });
          } else {
            /* istanbul ignore next -- defensive: with selectionMode="single" the emitted key always belongs to a rendered node (found in treeData); real deselection goes through handleNodeSelect */
            setTreeSelected(null);
          }
        }}
        nodeTemplate={useNodeTemplate}
        emptyMessage={
          <div
            style={{
              textAlign: "center",
              maxWidth: 220,
              margin: "0 auto",
              whiteSpace: "normal",
            }}
          >
            {t(
              "Não há dados cadastrados. Se isso for inesperado, por favor, contate o suporte.",
            )}
          </div>
        }
      />
    </>
  );
}

AssetsTree.propTypes = {
  showFilterHighlight: PropTypes.bool,
};

export default AssetsTree;

// Função utilitária: retorna as keys dos ancestrais (raiz -> pai) do nó com a
// key dada, percorrendo a treeData já carregada. Retorna [] quando o nó é raiz
// (encontrado no topo, sem ancestrais) e null quando o nó não está carregado —
// permite distinguir "raiz" de "ausente" sem quebrar strings de key.
function findAncestorKeys(nodes, key, trail = []) {
  if (!nodes) return null;
  for (const node of nodes) {
    if (node.key === key) return trail;
    if (node.children) {
      const found = findAncestorKeys(node.children, key, [...trail, node.key]);
      if (found) return found;
    }
  }
  return null;
}

// Função utilitária para encontrar nó pela key
function findNodeByKey(nodes, key) {
  /* istanbul ignore next -- defensive: callers always pass a truthy key from a rendered node, and the recursive call is guarded by `if (node.children)`, so key/nodes are never falsy here */
  if (!key || !nodes) return null;
  for (const node of nodes) {
    if (node.key === key) return node;
    if (node.children) {
      const found = findNodeByKey(node.children, key);
      if (found) return found;
    }
  }
  return null;
}
